import { plans, type ExerciseId } from '@repwise/domain';
import { diag } from '../diag/log.ts';
import type { RepwiseStore } from '@repwise/sync';

/**
 * Hosted configs on the device (ADR-0020). The catalogue index is fetched when online and remembered for offline
 * use. A chosen plan is fetched by its content-hashed URL, checked (hash + validatePlan), and stored immutably as
 * `plan:<id>@<version>`; the engine reads only that stored copy, so a session never waits on the network.
 */
export interface ConfigIndexEntry {
  id: string;
  version: number;
  title: string;
  summary: string;
  daysPerWeek: number;
  equipment: string[];
  basedOn: string;
  url: string;
  hash: string;
  bytes: number;
  /** J27: what changed in this version. */
  changes?: string;
}
export interface ConfigIndex {
  schema: 1;
  generatedAt: string;
  exercises: { version: number; url: string; hash: string };
  rpeTable: { version: number; url: string; hash: string };
  /** J24: Repwise's own exercise guides. */
  guides?: { version: number; url: string; hash: string };
  plans: ConfigIndexEntry[];
  /** Earlier versions, kept forever so history replays and a new device can load a user's version (ADR-0023). */
  archive?: Pick<ConfigIndexEntry, 'id' | 'version' | 'url' | 'hash' | 'bytes'>[];
}

/** A plan version's file, current or archived. */
export const entryFor = (index: ConfigIndex | null | undefined, id: string, version: number) =>
  index?.plans.find((p) => p.id === id && p.version === version) ?? index?.archive?.find((p) => p.id === id && p.version === version);

const BASE = ((import.meta.env.VITE_API_BASE as string | undefined) ?? '').replace(/\/api$/, '').replace(/\/$/, '');
const INDEX_KEY = 'config:index';
const planKey = (id: string, version: number) => `plan:${id}@${version}`;

async function sha10(text: string): Promise<string> {
  const d = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(d)].map((b) => b.toString(16).padStart(2, '0')).join('').slice(0, 10);
}

/** Latest index when online; otherwise the last one seen on this device (or null on a never-online device). */
export async function loadIndex(store: RepwiseStore, fetcher: typeof fetch = fetch): Promise<{ index: ConfigIndex | null; fresh: boolean }> {
  try {
    const res = await fetcher(`${BASE}/config/index.json`, { cache: 'no-cache' });
    if (res.ok) {
      const index = (await res.json()) as ConfigIndex;
      if (index.schema === 1 && Array.isArray(index.plans)) {
        await store.setMeta(INDEX_KEY, index);
        diag('config', 'index', { fresh: true, plans: index.plans.length });
        return { index, fresh: true };
      }
    }
  } catch {
    /* offline: fall through to the stored copy */
  }
  const stored = (await store.meta<ConfigIndex>(INDEX_KEY)) ?? null;
  diag('config', 'index', { fresh: false, stored: !!stored });
  return { index: stored, fresh: false };
}

export type LoadPlanResult = { ok: true; plan: plans.PlanConfig; cached: boolean } | { ok: false; error: 'offline' | 'invalid' | 'mismatch' };

/** The stored copy if this device has it; otherwise download, verify and store it. */
export async function loadPlan(store: RepwiseStore, entry: Pick<ConfigIndexEntry, 'id' | 'version' | 'url' | 'hash'>, fetcher: typeof fetch = fetch): Promise<LoadPlanResult> {
  const stored = await store.meta<plans.PlanConfig>(planKey(entry.id, entry.version));
  if (stored) return { ok: true, plan: stored, cached: true };
  let text: string;
  try {
    const res = await fetcher(`${BASE}${entry.url}`);
    if (!res.ok) return { ok: false, error: 'offline' };
    text = await res.text();
  } catch {
    return { ok: false, error: 'offline' };
  }
  if ((await sha10(text)) !== entry.hash) return (diag('config', 'plan_mismatch', { id: entry.id, version: entry.version }, 'warn'), { ok: false, error: 'mismatch' });
  let plan: plans.PlanConfig;
  try {
    plan = JSON.parse(text) as plans.PlanConfig;
  } catch {
    return { ok: false, error: 'invalid' };
  }
  if (plan.id !== entry.id || plan.version !== entry.version || plans.validatePlan(plan).length) return { ok: false, error: 'invalid' };
  await store.setMeta(planKey(plan.id, plan.version), plan);
  diag('config', 'plan_downloaded', { id: plan.id, version: plan.version, bytes: text.length });
  return { ok: true, plan, cached: false };
}

export const storedPlan = (store: RepwiseStore, id: string, version: number) => store.meta<plans.PlanConfig>(planKey(id, version));
export type { ExerciseId };

const SHARED = { rpe: 'config:rpe-table', exercises: 'config:exercises' } as const;

/** The RPE table and exercise catalogue: fetched with the index when needed, verified, stored for offline use. */
export interface Guide {
  steps: string[];
  faults: string[];
}
export interface Guides {
  schema: 1;
  version: number;
  guides: Record<string, Guide>;
}

export async function loadShared(store: RepwiseStore, index: ConfigIndex | null, fetcher: typeof fetch = fetch): Promise<{ table: plans.RpeTable | null; catalogue: plans.ExerciseCatalogue | null; guides: Guides | null }> {
  const get = async <T>(key: string, entry: { url: string; hash: string } | undefined): Promise<T | null> => {
    const have = await store.meta<{ hash: string; data: T }>(key);
    if (have && (!entry || have.hash === entry.hash)) return have.data;
    if (!entry) return have?.data ?? null;
    try {
      const res = await fetcher(`${BASE}${entry.url}`);
      const text = await res.text();
      if (!res.ok || (await sha10(text)) !== entry.hash) return have?.data ?? null;
      const data = JSON.parse(text) as T;
      await store.setMeta(key, { hash: entry.hash, data });
      return data;
    } catch {
      return have?.data ?? null;
    }
  };
  const [table, catalogue, guides] = await Promise.all([get<plans.RpeTable>(SHARED.rpe, index?.rpeTable), get<plans.ExerciseCatalogue>(SHARED.exercises, index?.exercises), get<Guides>('config:guides', index?.guides)]);
  return { table, catalogue, guides };
}
